import { useEffect, useMemo, useState, useRef } from "react";
import { Pencil, Trash2, X, RotateCcw } from "lucide-react";
import api from "../api";
import "./software.css";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

/* ================= CONSTANTS ================= */
const LOCATIONS = ["Surat", "Ahmedabad", "Gandhinagar"];

const SOFTWARE_LIST = [
  "AEC",
  "Zwcad",
  "BIM Collaborator Pro",
  "SketchUp",
  "Adobe",
  "Bluebeam",
  "Microsoft 365",
  "D5",
  "Project Plan 3",
  "Primus",
  "CADMATE ",
  "BricsCAD",
  "Tally",
];

const SOFTWARE_TYPES = [
  "Drafting",
  "Sketching",
  "3D Modeling",
  "Rendering",
  "Documentation",
  "PDF Viewer, Markupt Edioting",
  "Estimation & BOQ",
  "BIM Modeling",
  "Accounting & Invoicing",
  "Graphics Design",
];

const SOFTWARE_LOGOS = {
  AEC: "/software-logos/aec.png",
  Zwcad: "/software-logos/zwcad.png",
  "BIM Collaborator Pro": "/software-logos/revit.png",
  SketchUp: "/software-logos/sketchup.png",
  Adobe: "/software-logos/adobe.png",
  Bluebeam: "/software-logos/bluebeam.png",
  "Microsoft 365": "/software-logos/microsoft.png",
  D5: "/software-logos/d5.png",
  "Project Plan 3": "/software-logos/project-plan-3.png",
  Primus: "/software-logos/primus.jpeg",
  Cadmate: "/software-logos/cadmate.png",
  BricsCAD: "/software-logos/bricscad.png",
  Tally: "/software-logos/tally.png",
};

export default function SoftwareAdmin() {
  const [software, setSoftware] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedSoftware, setSelectedSoftware] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const [isRenew, setIsRenew] = useState(false);
  const [renewId, setRenewId] = useState(null);
  const navigate = useNavigate();
  const detailRef = useRef(null);
  /* ================= HELPERS ================= */
  const getExpiryInfo = (endDate) => {
    if (!endDate)
      return { text: "Lifetime License", color: "#1d4ed8", bg: "#e0e7ff" };

    const diff = Math.ceil(
      (new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    if (diff < 0) return { text: "Expired", color: "#dc2626", bg: "#fee2e2" };

    if (diff <= 30)
      return {
        text: `${diff} Days Left (Expiring Soon)`,
        color: "#ea580c",
        bg: "#ffedd5",
      };

    return { text: `${diff} Days Left`, color: "#16a34a", bg: "#dcfce7" };
  };

  const userOptions = users.map((u) => ({
    value: u._id,
    label: u.name,
  }));

  /* ================= FORM ================= */
  const emptyForm = {
    softwareName: "",
    softwareType: "",
    version: "",
    licenseKey: "",
    seats: 1,
    seatsData: [],
    loginId: "",
    password: "",
    vendor: "",
    vendorContactNo: "",
    vendorEmail: "",
    invoiceNo: "",
    location: "",
    validity: "",
    purchaseDate: "",
    endDate: "",
    price: "",
    description: "",
  };

  const [form, setForm] = useState(emptyForm);

  /* ================= LOAD ================= */
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const s = await api.get("/software");
    const u = await api.get("/users");
    setSoftware((s.data || []).filter((l) => !l.archivedAt));
    setUsers(u.data || []);
  };

  /* ================= SEATS ================= */
  const handleSeatsChange = (count) => {
    const seats = Number(count);
    const updatedSeats = Array.from({ length: seats }).map((_, i) => ({
      seatNo: i + 1,
      user: "",
      pcName: "",
      ipAddress: "",
    }));

    setForm({ ...form, seats, seatsData: updatedSeats });
  };

  /* ================= SUMMARY ================= */
  const softwareSummary = useMemo(() => {
    const map = {};
    software.forEach((l) => {
      if (!map[l.softwareName]) {
        map[l.softwareName] = {
          name: l.softwareName,
          type: l.softwareType,
          total: 0,
          used: 0,
          available: 0,
          cities: { Surat: 0, Ahmedabad: 0, Gandhinagar: 0 },
        };
      }

      const seats = Number(l.seats || 1);

      // ✅ seat-wise used count
      const usedSeats = (l.seatsData || []).filter((s) => s.user).length;

      // ✅ available seats
      const availableSeats = Math.max(seats - usedSeats, 0);

      map[l.softwareName].total += seats;
      map[l.softwareName].used += usedSeats;
      map[l.softwareName].available += availableSeats;

      if (map[l.softwareName].cities[l.location] !== undefined) {
        map[l.softwareName].cities[l.location] += seats;
      }
    });

    return Object.values(map);
  }, [software]);

  const selectedLicenses = useMemo(() => {
    if (!selectedSoftware) return [];
    return software.filter((s) => s.softwareName === selectedSoftware);
  }, [software, selectedSoftware]);

  /* ================= OPEN ================= */
  const openAdd = () => {
    setForm(emptyForm);
    setIsEdit(false);
    setIsRenew(false);
    setEditId(null);
    setRenewId(null);
    setShowModal(true);
  };

  const openEdit = (l) => {
    setForm({
      ...emptyForm,
      ...l,
      seats: l.seats || 1,
      seatsData: l.seatsData || [],
      purchaseDate: l.purchaseDate?.slice(0, 10),
      endDate: l.endDate?.slice(0, 10),
    });
    setIsEdit(true);
    setIsRenew(false);
    setEditId(l._id);
    setShowModal(true);
  };

  const openRenew = (l) => {
    setForm({
      ...emptyForm,
      ...l,
      purchaseDate: l.purchaseDate?.slice(0, 10),
      endDate: l.endDate?.slice(0, 10),
    });
    setIsRenew(true);
    setIsEdit(false);
    setRenewId(l._id);
    setShowModal(true);
  };

  /* ================= SAVE ================= */
  const handleSave = async (e) => {
    e.preventDefault();

    const cleanPrice = form.price
      ? Number(form.price.toString().replace(/,/g, ""))
      : 0;

    // ✅ VALIDATION: only assigned seats need PC/IP
    for (let i = 0; i < form.seatsData.length; i++) {
      const seat = form.seatsData[i];

      if (seat.user) {
        if (!seat.pcName || !seat.ipAddress) {
          alert(`Seat ${i + 1}: PC Name & IP Address required`);
          return;
        }
      }
    }

    try {
      if (isRenew) {
        await api.post(`/software/renew/${renewId}`, {
          newStartDate: form.purchaseDate,
          newEndDate: form.endDate,
          renewalPrice: cleanPrice,
          invoiceNo: form.invoiceNo,
        });
      } else {
        const assignedUsers = form.seatsData.map((s) => s.user).filter(Boolean);

        const payload = {
          ...form,
          price: cleanPrice,
          assignedUsers,
          seatsData: form.seatsData,
        };

        if (isEdit) {
          await api.put(`/software/license/${editId}`, payload);
        } else {
          await api.post(`/software/license`, payload);
        }
      }

      setShowModal(false);
      setForm(emptyForm);
      setIsEdit(false);
      setIsRenew(false);
      setEditId(null);
      setRenewId(null);
      loadData();
    } catch (err) {
      console.error(err);
      alert("Error while saving license");
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this license?")) return;
    await api.delete(`/software/license/${id}`);
    loadData();
  };

  return (
    <div className="software-page">
      {/* HEADER */}
      <div className="software-header">
        <h2>Software License Master</h2>

        <div className="header-actions">
          <button type="button" className="primary-btn" onClick={openAdd}>
            + Add License
          </button>

          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate("/software/archived")}
          >
            📦 Archived Licenses
          </button>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="software-cards">
        {softwareSummary.map((s) => (
          <div
            key={s.name}
            data-software={s.name.toLowerCase().replace(/\s+/g, "-")} // 👈 IMPORTANT
            className={`software-card ${
              selectedSoftware === s.name ? "active" : ""
            }`}
            onClick={() => {
              const next = selectedSoftware === s.name ? null : s.name;

              setSelectedSoftware(next);

              if (next) {
                setTimeout(() => {
                  detailRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }, 150);
              }
            }}
          >
            <div
              className="software-card-header logo-bg-header"
              style={{
                backgroundImage: `url(${SOFTWARE_LOGOS[s.name]})`,
              }}
            >
              <div className="logo-text-overlay">
                <h3>{s.name} </h3>
                <p>{s.type || "-"}</p>
              </div>
            </div>

            <div className="card-stats">
              <div className="stat used">
                <span>Used</span>
                <strong>{s.used}</strong>
              </div>
              <div className="stat available">
                <span>Available</span>
                <strong>{s.available}</strong>
              </div>
              <div className="stat surat">
                <span>Surat</span>
                <strong>{s.cities.Surat}</strong>
              </div>

              <div className="stat ahmedabad">
                <span>Ahmedabad</span>
                <strong>{s.cities.Ahmedabad}</strong>
              </div>

              <div className="stat gandhinagar">
                <span>Gandhinagar</span>
                <strong>{s.cities.Gandhinagar}</strong>
              </div>
            </div>
            <div className="stat total big-total">
              <span>Total Licenses</span>
              <strong>{s.total}</strong>
            </div>
            {s.expiringSoon > 0 && (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 14,
                  background: "#fff7ed",
                  border: "1px dashed #fdba74",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#ea580c",
                    marginBottom: 6,
                    textAlign: "center",
                  }}
                >
                  ⏳ Expiring in 30 Days: {s.expiringSoon}
                </div>

                {s.expiringList.map((e, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12,
                      padding: "4px 0",
                      color: "#7c2d12",
                      borderBottom: "1px dashed #fed7aa",
                    }}
                  >
                    <span>🔑 {e.licenseKey}</span>
                    <span>{e.city}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* LICENSE DETAILS */}
      {selectedSoftware && (
        <div ref={detailRef}>
          <h3 className="detail-title">{selectedSoftware} – All Licenses</h3>

          <div className="license-blocks">
            {selectedLicenses.map((l) => (
              <div className="license-card" key={l._id}>
                <div className="license-card-top">
                  <span className="license-title">License Details</span>
                  <div className="license-actions">
                    <button onClick={() => openEdit(l)}>
                      <Pencil size={14} />
                    </button>{" "}
                    <button onClick={() => openRenew(l)} title="Renew License">
                      <RotateCcw size={14} />+{" "}
                    </button>
                    <button
                      className="danger"
                      onClick={() => handleDelete(l._id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="lc-row">
                  <span>Software Type</span>
                  <strong>{l.softwareType}</strong>
                </div>

                <div className="lc-row">
                  <span>Version</span>
                  <strong>{l.version}</strong>
                </div>

                <div className="lc-row">
                  <span>License Key</span>
                  <strong>{l.licenseKey}</strong>
                </div>
                <div className="lc-row">
                  <span>Seats</span>
                  <strong>{l.seats}</strong>
                </div>

                <div className="lc-row">
                  <span>Login ID</span>
                  <strong>{l.loginId}</strong>
                </div>

                <div className="lc-row">
                  <span>Password</span>
                  <strong>{l.password}</strong>
                </div>

                <div className="lc-row">
                  <span>User</span>
                  <strong>
                    {l.assignedUsers?.length
                      ? l.assignedUsers.map((u) => u.name).join(", ")
                      : "Stock"}
                  </strong>
                </div>

                {l.seatsData?.map((s, i) => (
                  <div className="seat-box" key={i}>
                    <div className="seat-title">
                      Seat {i + 1} – {s.user?.name || "Available"}
                    </div>

                    <div className="seat-meta">
                      <span className="seat-chip">💻 {s.pcName}</span>
                      <span className="seat-chip">🌐 {s.ipAddress}</span>
                    </div>
                  </div>
                ))}

                <div className="lc-row">
                  <span>Location</span>
                  <strong>{l.location}</strong>
                </div>

                <div className="lc-row">
                  <span>Validity</span>
                  <strong>{l.validity}</strong>
                </div>

                <div className="lc-row">
                  <span>Start Date</span>
                  <strong>{l.purchaseDate?.slice(0, 10)}</strong>
                </div>

                <div className="lc-row">
                  <span>Expiry Date</span>
                  <strong>{l.endDate?.slice(0, 10)}</strong>
                </div>

                <div className="lc-row">
                  <span>Vendor</span>
                  <strong>{l.vendor}</strong>
                </div>

                <div className="lc-row">
                  <span>Vendor Contact</span>
                  <strong>{l.vendorContactNo}</strong>
                </div>

                <div className="lc-row">
                  <span>Vendor Email</span>
                  <strong>{l.vendorEmail}</strong>
                </div>

                <div className="lc-row">
                  <span>Invoice No</span>
                  <strong>{l.invoiceNo}</strong>
                </div>

                <div className="lc-row">
                  <span>Description</span>
                  <strong>{l.description}</strong>
                </div>

                <div className="lc-price">₹ {l.price || 0}</div>
                {(() => {
                  const expiry = getExpiryInfo(l.endDate);

                  return (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "8px 12px",
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 800,
                        textAlign: "center",
                        background: expiry.bg,
                        color: expiry.color,
                      }}
                    >
                      ⏳ {expiry.text}
                    </div>
                  );
                })()}

                {/* 📦 Archived Renewals – Last 6 Months */}
                {l.renewalHistory?.length > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 10,
                      borderRadius: 12,
                      background: "#f8fafc",
                      border: "1px dashed #cbd5e1",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        marginBottom: 6,
                        fontSize: 13,
                      }}
                    >
                      📦 Archived Renewals (Last 6 Months)
                    </div>

                    {l.renewalHistory
                      .filter((r) => {
                        const sixMonthsAgo = new Date();
                        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                        return new Date(r.renewedAt) >= sixMonthsAgo;
                      })
                      .map((r, i) => (
                        <div
                          key={i}
                          style={{
                            fontSize: 12,
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "4px 0",
                            borderBottom: "1px dashed #e5e7eb",
                          }}
                        >
                          <span>
                            {r.startDate?.slice(0, 10)} →{" "}
                            {r.endDate?.slice(0, 10)}
                          </span>
                          <span>₹ {r.price}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            {/* HEADER */}
            <div className="modal-header">
              <h3>
                {isRenew
                  ? "Renew License"
                  : isEdit
                  ? "Edit License"
                  : "Add License"}
              </h3>
              <button onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            {/* FORM */}
            <form onSubmit={handleSave} className="modal-form modern-form">
              {/* ================= SOFTWARE DETAILS ================= */}
              <div className="form-section">
                <h4>Software Details</h4>
                <div className="form-grid-3">
                  <select
                    value={form.softwareName}
                    onChange={(e) =>
                      setForm({ ...form, softwareName: e.target.value })
                    }
                    required
                  >
                    <option value="">-- Select Software --</option>
                    {SOFTWARE_LIST.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>

                  <select
                    value={form.softwareType}
                    onChange={(e) =>
                      setForm({ ...form, softwareType: e.target.value })
                    }
                  >
                    <option value="">-- Select Software Type --</option>
                    {SOFTWARE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>

                  <input
                    placeholder="Version"
                    value={form.version}
                    onChange={(e) =>
                      setForm({ ...form, version: e.target.value })
                    }
                  />

                  <input
                    placeholder="License Key"
                    value={form.licenseKey}
                    disabled={isRenew}
                    onChange={(e) =>
                      setForm({ ...form, licenseKey: e.target.value })
                    }
                  />
                  <input
                    placeholder="Seats"
                    value={form.seats}
                    type="number"
                    min={1}
                    onChange={(e) => handleSeatsChange(e.target.value)}
                  />
                </div>
              </div>

              {/* ================= LOGIN / ASSIGNMENT ================= */}
              <div className="form-section">
                <h4>Access & Assignment</h4>
                <div className="form-grid-3">
                  <input
                    placeholder="Login ID"
                    value={form.loginId}
                    onChange={(e) =>
                      setForm({ ...form, loginId: e.target.value })
                    }
                  />

                  <input
                    placeholder="Password"
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                  />

                  <div className="form-section">
                    <h4>Seat Wise Assignment</h4>

                    {form.seatsData.map((seat, index) => (
                      <div
                        key={index}
                        style={{
                          border: "1px dashed #cbd5e1",
                          padding: 12,
                          borderRadius: 10,
                          marginBottom: 10,
                          background: "#f8fafc",
                        }}
                      >
                        <strong>Seat {index + 1}</strong>

                        <div className="form-grid-3" style={{ marginTop: 8 }}>
                          {/* USER */}
                          <Select
                            options={userOptions}
                            placeholder="Select User"
                            value={userOptions.find(
                              (u) => u.value === seat.user
                            )}
                            onChange={(selected) => {
                              const updated = [...form.seatsData];
                              updated[index].user = selected?.value || "";
                              setForm({ ...form, seatsData: updated });
                            }}
                          />

                          {/* PC NAME */}
                          <input
                            placeholder="PC Name"
                            value={seat.pcName}
                            onChange={(e) => {
                              const updated = [...form.seatsData];
                              updated[index].pcName = e.target.value;
                              setForm({ ...form, seatsData: updated });
                            }}
                          />

                          {/* IP ADDRESS */}
                          <input
                            placeholder="IP Address"
                            value={seat.ipAddress}
                            onChange={(e) => {
                              const updated = [...form.seatsData];
                              updated[index].ipAddress = e.target.value;
                              setForm({ ...form, seatsData: updated });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ================= SYSTEM DETAILS ================= */}
              <div className="form-section">
                <h4>System & Location</h4>
                <div className="form-grid-3">
                  <select
                    value={form.location}
                    onChange={(e) =>
                      setForm({ ...form, location: e.target.value })
                    }
                  >
                    <option value="">-- Select Location --</option>
                    {LOCATIONS.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                  {/* ✅ VALIDITY (ADD THIS) */}
                  <select
                    value={form.validity}
                    onChange={(e) =>
                      setForm({ ...form, validity: e.target.value })
                    }
                    required
                  >
                    <option value="">-- Select Validity --</option>
                    <option value="Lifetime">Lifetime</option>
                    <option value="1 Year">1 Year</option>
                    <option value="2 Years">2 Years</option>
                    <option value="3 Years">3 Years</option>
                    <option value="Subscription">Subscription</option>
                  </select>
                </div>
              </div>

              {/* ================= PURCHASE & VENDOR ================= */}
              <div className="form-section">
                <h4>Purchase & Vendor Details</h4>
                <div className="form-grid-3">
                  <input
                    placeholder="Price"
                    value={form.price}
                    onChange={(e) =>
                      setForm({ ...form, price: e.target.value })
                    }
                  />

                  <input
                    placeholder="Invoice No"
                    value={form.invoiceNo}
                    onChange={(e) =>
                      setForm({ ...form, invoiceNo: e.target.value })
                    }
                  />

                  <input
                    placeholder="Vendor Name"
                    value={form.vendor}
                    onChange={(e) =>
                      setForm({ ...form, vendor: e.target.value })
                    }
                  />

                  <input
                    placeholder="Vendor Contact No"
                    value={form.vendorContactNo}
                    onChange={(e) =>
                      setForm({ ...form, vendorContactNo: e.target.value })
                    }
                  />

                  <input
                    placeholder="Vendor Email"
                    value={form.vendorEmail}
                    onChange={(e) =>
                      setForm({ ...form, vendorEmail: e.target.value })
                    }
                  />

                  <input
                    type="date"
                    value={form.purchaseDate}
                    onChange={(e) =>
                      setForm({ ...form, purchaseDate: e.target.value })
                    }
                  />

                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) =>
                      setForm({ ...form, endDate: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* ================= DESCRIPTION ================= */}
              <div className="form-section">
                <h4>Description</h4>
                <textarea
                  className="full-textarea"
                  placeholder="Notes / Remarks"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>
              {/* ================= 🔁 RENEWAL DETAILS ================= */}
              {isRenew && (
                <div className="form-section">
                  <h4>Renewal Details</h4>

                  <div className="form-grid-3">
                    <input
                      type="date"
                      required
                      value={form.purchaseDate}
                      onChange={(e) =>
                        setForm({ ...form, purchaseDate: e.target.value })
                      }
                    />

                    <input
                      type="date"
                      required
                      value={form.endDate}
                      onChange={(e) =>
                        setForm({ ...form, endDate: e.target.value })
                      }
                    />

                    <input
                      placeholder="Renewal Price"
                      value={form.price}
                      onChange={(e) =>
                        setForm({ ...form, price: e.target.value })
                      }
                    />

                    <input
                      placeholder="Invoice No"
                      value={form.invoiceNo}
                      onChange={(e) =>
                        setForm({ ...form, invoiceNo: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}

              {/* FOOTER */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button className="primary-btn" type="submit">
                  {isRenew
                    ? "Renew License"
                    : isEdit
                    ? "Update License"
                    : "Save License"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
